import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Upload, FileText, CreditCard, Receipt, Building2, 
  CheckCircle, AlertCircle, Loader2, Trash2, Eye, 
  ArrowRight, RefreshCw, FileSpreadsheet, Archive,
  FolderOpen, Plus, X, FileImage
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";

const formatCurrency = (amount) => {
  if (!amount) return "₺0,00";
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const docTypes = {
  bank_statement: { label: "Banka Ekstresi", icon: Building2, color: "sky" },
  credit_card_statement: { label: "Kredi Kartı Ekstresi", icon: CreditCard, color: "amber" },
  invoice: { label: "Fatura", icon: Receipt, color: "emerald" },
  excel_data: { label: "Excel Verisi", icon: FileSpreadsheet, color: "green" },
  unknown: { label: "Diğer", icon: FileText, color: "zinc" }
};

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return FileText;
    case 'xlsx':
    case 'xls': return FileSpreadsheet;
    case 'zip': return Archive;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp': return FileImage;
    default: return FileText;
  }
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [applying, setApplying] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocuments(response.data);
    } catch (error) {
      toast.error("Belgeler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const supportedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'xlsx', 'xls', 'zip'];

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return supportedExtensions.includes(ext);
    });
    
    if (validFiles.length !== droppedFiles.length) {
      toast.warning(`${droppedFiles.length - validFiles.length} dosya desteklenmiyor`);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Lütfen dosya seçin");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(`${API}/upload/bulk`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setUploadResult(response.data);
      
      const { processed, transactions_created, transactions_skipped, accounts_created, errors } = response.data;
      
      if (processed > 0) {
        toast.success(
          `${processed} dosya işlendi: ${transactions_created} kayıt eklendi` + 
          (transactions_skipped > 0 ? `, ${transactions_skipped} tekrar atlandı` : '') +
          (accounts_created?.length > 0 ? `, ${accounts_created.length} hesap oluşturuldu` : '')
        );
      }
      
      if (errors?.length > 0) {
        toast.warning(`${errors.length} dosyada hata oluştu`);
      }
      
      setSelectedFiles([]);
      fetchDocuments();
    } catch (error) {
      const message = error.response?.data?.detail || "Yükleme başarısız";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleApply = async (docId) => {
    setApplying(docId);
    try {
      const response = await axios.post(`${API}/documents/${docId}/apply`, {}, { withCredentials: true });
      
      const created = response.data.created?.length || 0;
      const errors = response.data.errors?.length || 0;
      
      if (created > 0) {
        toast.success(`${created} kayıt oluşturuldu`);
      }
      if (errors > 0) {
        toast.warning(`${errors} kayıt oluşturulamadı`);
      }
      
      fetchDocuments();
    } catch (error) {
      toast.error("İşlem uygulanamadı");
    } finally {
      setApplying(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Bu belgeyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      toast.success("Belge silindi");
      fetchDocuments();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Belge Yükleme</h1>
        <p className="text-zinc-500 text-sm mt-1">
          Banka ekstresi, fatura, Excel veya ZIP dosyası yükleyin - otomatik analiz ve kayıt
        </p>
      </div>

      {/* Supported Formats */}
      <div className="flex flex-wrap gap-2">
        {[
          { ext: 'PDF', icon: FileText, color: 'rose' },
          { ext: 'Excel', icon: FileSpreadsheet, color: 'emerald' },
          { ext: 'Görsel', icon: FileImage, color: 'sky' },
          { ext: 'ZIP', icon: Archive, color: 'amber' }
        ].map(({ ext, icon: Icon, color }) => (
          <div key={ext} className={`flex items-center gap-1.5 px-3 py-1.5 bg-${color}-400/10 border border-${color}-400/20 rounded-full`}>
            <Icon className={`w-3.5 h-3.5 text-${color}-400`} />
            <span className={`text-xs text-${color}-400`}>{ext}</span>
          </div>
        ))}
      </div>

      {/* Upload Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
        {/* Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed rounded-sm p-8 text-center transition-all ${
            dragOver 
              ? 'border-amber-400 bg-amber-400/10 scale-[1.01]' 
              : 'border-zinc-700 hover:border-zinc-600'
          }`}
        >
          <Upload className={`w-12 h-12 mx-auto mb-4 transition-colors ${dragOver ? 'text-amber-400' : 'text-zinc-500'}`} />
          <p className="text-zinc-400 mb-2">
            Dosyaları sürükleyip bırakın
          </p>
          <p className="text-zinc-600 text-sm mb-4">
            PDF, Excel, Görsel veya ZIP desteklenir
          </p>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.zip"
              onChange={handleFileChange}
              multiple
              className="hidden"
              data-testid="file-input"
            />
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-50 rounded-sm transition-colors">
              <Plus className="w-4 h-4" />
              Dosya Seç
            </span>
          </label>
        </div>

        {/* Selected Files */}
        <AnimatePresence>
          {selectedFiles.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-zinc-400">
                  {selectedFiles.length} dosya seçildi
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedFiles([])}
                  className="text-zinc-500 hover:text-zinc-300"
                >
                  Tümünü Temizle
                </Button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {selectedFiles.map((file, index) => {
                  const Icon = getFileIcon(file.name);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex items-center justify-between p-3 bg-zinc-950 border border-zinc-800 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-amber-400" />
                        <div>
                          <p className="text-sm text-zinc-50">{file.name}</p>
                          <p className="text-xs text-zinc-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-zinc-800 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-zinc-500 hover:text-zinc-300" />
                      </button>
                    </motion.div>
                  );
                })}
              </div>

              {/* Upload Button */}
              <div className="mt-4">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950"
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Yükle ve Analiz Et ({selectedFiles.length} dosya)
                    </>
                  )}
                </Button>
                
                {uploading && (
                  <div className="mt-3">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-zinc-500 mt-1 text-center">{uploadProgress}%</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload Result Summary */}
        <AnimatePresence>
          {uploadResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-4 p-4 bg-zinc-950 border border-zinc-800 rounded-sm"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-zinc-50">İşlem Sonucu</h4>
                <button onClick={() => setUploadResult(null)} className="text-zinc-500 hover:text-zinc-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-zinc-900 rounded-sm">
                  <p className="text-xs text-zinc-500">Dosya İşlendi</p>
                  <p className="text-lg font-mono text-emerald-400">{uploadResult.processed}</p>
                </div>
                <div className="p-3 bg-zinc-900 rounded-sm">
                  <p className="text-xs text-zinc-500">Kayıt Eklendi</p>
                  <p className="text-lg font-mono text-amber-400">{uploadResult.transactions_created}</p>
                </div>
                <div className="p-3 bg-zinc-900 rounded-sm">
                  <p className="text-xs text-zinc-500">Tekrar Atlandı</p>
                  <p className="text-lg font-mono text-zinc-400">{uploadResult.transactions_skipped}</p>
                </div>
                <div className="p-3 bg-zinc-900 rounded-sm">
                  <p className="text-xs text-zinc-500">Hata</p>
                  <p className="text-lg font-mono text-rose-400">{uploadResult.errors?.length || 0}</p>
                </div>
              </div>

              {/* Auto-created accounts */}
              {uploadResult.accounts_created?.length > 0 && (
                <div className="mt-3 p-3 bg-emerald-400/10 border border-emerald-400/20 rounded-sm">
                  <p className="text-xs text-emerald-400 mb-2">Otomatik Oluşturulan Hesaplar:</p>
                  {uploadResult.accounts_created.map((acc, i) => (
                    <p key={i} className="text-sm text-zinc-50">
                      {acc.bank || 'Banka'} - {acc.iban || acc.account_no || 'Hesap'}
                    </p>
                  ))}
                </div>
              )}

              {/* Errors */}
              {uploadResult.errors?.length > 0 && (
                <div className="mt-3 p-3 bg-rose-400/10 border border-rose-400/20 rounded-sm">
                  <p className="text-xs text-rose-400 mb-2">Hatalar:</p>
                  {uploadResult.errors.slice(0, 5).map((err, i) => (
                    <p key={i} className="text-xs text-rose-400/70">
                      {err.file}: {err.error}
                    </p>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <p className="text-xs text-rose-400/50 mt-1">
                      +{uploadResult.errors.length - 5} daha...
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Documents List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-lg font-outfit font-medium text-zinc-50">Yüklenen Belgeler</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchDocuments}
            className="text-zinc-400 hover:text-zinc-50"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <div className="divide-y divide-zinc-800">
          {documents.map((doc, index) => {
            const docTypeInfo = docTypes[doc.doc_type] || docTypes.unknown;
            const Icon = docTypeInfo.icon;
            const analysis = doc.analysis || {};
            
            return (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className="p-4 hover:bg-zinc-800/50 transition-colors"
                data-testid={`document-${doc.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-sm bg-${docTypeInfo.color}-400/10`}>
                      <Icon className={`w-6 h-6 text-${docTypeInfo.color}-400`} />
                    </div>
                    <div>
                      <p className="text-zinc-50 font-medium">{doc.original_filename}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-zinc-500">{docTypeInfo.label}</span>
                        {doc.from_zip && (
                          <>
                            <span className="text-xs text-zinc-600">|</span>
                            <span className="text-xs text-amber-400/70 flex items-center gap-1">
                              <Archive className="w-3 h-3" />
                              {doc.from_zip}
                            </span>
                          </>
                        )}
                        <span className="text-xs text-zinc-600">|</span>
                        <span className="text-xs text-zinc-500">
                          {new Date(doc.created_at).toLocaleDateString('tr-TR')}
                        </span>
                        {doc.processed ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            Analiz Edildi
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-rose-400">
                            <AlertCircle className="w-3 h-3" />
                            Analiz Edilemedi
                          </span>
                        )}
                        {doc.transactions_created > 0 && (
                          <span className="text-xs text-amber-400">
                            +{doc.transactions_created} kayıt
                          </span>
                        )}
                        {doc.account_id && (
                          <span className="flex items-center gap-1 text-xs text-sky-400">
                            <Building2 className="w-3 h-3" />
                            Hesap bağlı
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Summary info */}
                    {doc.processed && !analysis.error && (
                      <div className="text-right mr-4 hidden md:block">
                        {analysis.transactions && (
                          <p className="text-sm text-zinc-400">
                            {analysis.transactions.length} işlem
                          </p>
                        )}
                        {analysis.total && (
                          <p className="text-sm font-mono text-zinc-50">
                            {formatCurrency(analysis.total)}
                          </p>
                        )}
                        {analysis.total_spending && (
                          <p className="text-sm font-mono text-rose-400">
                            -{formatCurrency(analysis.total_spending)}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setPreviewDoc(doc)}
                      className="hover:bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                      data-testid={`view-doc-${doc.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    
                    {doc.processed && !doc.applied && !doc.transactions_created && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleApply(doc.id)}
                        disabled={applying === doc.id}
                        className="text-amber-400 hover:bg-amber-400/10"
                        data-testid={`apply-doc-${doc.id}`}
                      >
                        {applying === doc.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <ArrowRight className="w-4 h-4 mr-1" />
                            Uygula
                          </>
                        )}
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(doc.id)}
                      className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
          {documents.length === 0 && (
            <div className="p-12 text-center">
              <FolderOpen className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">Henüz belge yüklenmemiş</p>
              <p className="text-zinc-600 text-sm mt-1">PDF, Excel, görsel veya ZIP dosyası yükleyin</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              Belge Analiz Sonucu
            </DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-4">
              <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                <p className="text-sm text-zinc-400 mb-1">Dosya</p>
                <p className="text-zinc-50">{previewDoc.original_filename || previewDoc.filename}</p>
                {previewDoc.from_zip && (
                  <p className="text-xs text-amber-400/70 mt-1">ZIP'den: {previewDoc.from_zip}</p>
                )}
              </div>
              
              {previewDoc.analysis && !previewDoc.analysis.error ? (
                <>
                  {/* Account/Card Info */}
                  {(previewDoc.analysis.account_info || previewDoc.analysis.card_info) && (
                    <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                      <p className="text-sm text-zinc-400 mb-2">Hesap Bilgisi</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {previewDoc.analysis.account_info?.bank_name && (
                          <>
                            <span className="text-zinc-500">Banka:</span>
                            <span className="text-zinc-50">{previewDoc.analysis.account_info.bank_name}</span>
                          </>
                        )}
                        {previewDoc.analysis.card_info?.bank_name && (
                          <>
                            <span className="text-zinc-500">Banka:</span>
                            <span className="text-zinc-50">{previewDoc.analysis.card_info.bank_name}</span>
                          </>
                        )}
                        {previewDoc.analysis.account_info?.iban && (
                          <>
                            <span className="text-zinc-500">IBAN:</span>
                            <span className="text-zinc-50 font-mono text-xs">{previewDoc.analysis.account_info.iban}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    {previewDoc.analysis.opening_balance !== undefined && (
                      <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <p className="text-xs text-zinc-500">Açılış Bakiyesi</p>
                        <p className="text-lg font-mono text-zinc-50">{formatCurrency(previewDoc.analysis.opening_balance)}</p>
                      </div>
                    )}
                    {previewDoc.analysis.closing_balance !== undefined && (
                      <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <p className="text-xs text-zinc-500">Kapanış Bakiyesi</p>
                        <p className="text-lg font-mono text-zinc-50">{formatCurrency(previewDoc.analysis.closing_balance)}</p>
                      </div>
                    )}
                    {previewDoc.analysis.total_spending && (
                      <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <p className="text-xs text-zinc-500">Toplam Harcama</p>
                        <p className="text-lg font-mono text-rose-400">-{formatCurrency(previewDoc.analysis.total_spending)}</p>
                      </div>
                    )}
                    {previewDoc.analysis.minimum_payment && (
                      <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <p className="text-xs text-zinc-500">Asgari Ödeme</p>
                        <p className="text-lg font-mono text-amber-400">{formatCurrency(previewDoc.analysis.minimum_payment)}</p>
                      </div>
                    )}
                    {previewDoc.analysis.total && (
                      <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm col-span-2">
                        <p className="text-xs text-zinc-500">Fatura Tutarı</p>
                        <p className="text-lg font-mono text-zinc-50">{formatCurrency(previewDoc.analysis.total)}</p>
                      </div>
                    )}
                    {previewDoc.analysis.processed_transactions !== undefined && (
                      <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm col-span-2">
                        <p className="text-xs text-zinc-500">Excel'den İşlenen</p>
                        <p className="text-lg font-mono text-emerald-400">{previewDoc.analysis.processed_transactions} / {previewDoc.analysis.total_rows} satır</p>
                      </div>
                    )}
                  </div>

                  {/* Transactions */}
                  {previewDoc.analysis.transactions && previewDoc.analysis.transactions.length > 0 && (
                    <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm">
                      <p className="text-sm text-zinc-400 mb-3">İşlemler ({previewDoc.analysis.transactions.length})</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {previewDoc.analysis.transactions.map((tx, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-zinc-900 rounded-sm text-sm">
                            <div>
                              <p className="text-zinc-50">{tx.description || 'İşlem'}</p>
                              <p className="text-xs text-zinc-500">{tx.date} • {tx.category || 'Diğer'}</p>
                            </div>
                            <span className={`font-mono ${tx.type === 'income' ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {tx.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 bg-rose-400/10 border border-rose-400/20 rounded-sm">
                  <p className="text-rose-400">Belge analiz edilemedi</p>
                  {previewDoc.analysis?.error && (
                    <p className="text-sm text-rose-400/70 mt-1">{previewDoc.analysis.error}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
